import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Stack,
  Tooltip,
  alpha
} from "@mui/material";
import {
  Close,
  SecurityRounded,
  AdminPanelSettingsRounded,
  ManageAccountsRounded,
  ShieldRounded,
  InfoOutlined,
  PlaylistAddCheckRounded
} from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useSnackbar } from "notistack";

// Roles Update/Create Component
function RolesUpdate({
  handleModalClose,
  roleData,
  refetch,
  isNew
}) {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  // HOOK FORM
  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    setValue,
  } = useForm({
    mode: "onChange",
  });

  // QUERIES
  const { data: permissionsData } = useQuery(["permissions_list"], () =>
    axiosPrivate.get(`/roles/permissions`, { params: { page: -1 } }),
    { refetchOnWindowFocus: false }
  );

  // MUTATIONS
  const { mutate: create, isLoading: isCreating } = useMutation((formData) =>
    axiosPrivate.post(`/roles`, formData)
  );

  const { mutate: update, isLoading: isUpdating } = useMutation((formData) =>
    axiosPrivate.put(`/roles/${roleData.name}`, formData)
  );

  // DATA PROCESSING
  const permissions = permissionsData?.data?.data || [];
  const categories = [...new Set(permissions.map(p => p.category || 'System'))];

  // HELPERS
  const onSubmit = (formData) => {
    const action = isNew ? create : update;
    action(formData, {
      onSuccess: () => {
        enqueueSnackbar(`Role ${isNew ? 'registered' : 'updated'} successfully.`, { variant: "success" });
        handleModalClose();
        refetch();
      },
      onError: (err) => {
        const message = err?.response?.data?.error?.message || "Operation failed";
        enqueueSnackbar(message, { variant: "error" });
      }
    });
  };

  useEffect(() => {
    if (!isNew && roleData) {
      setValue("name", roleData.name);
      setValue("permissions_id", roleData.permissions || []);
    } else {
      setValue("permissions_id", []);
    }
  }, [isNew, roleData, setValue]);

  const actionLoading = isCreating || isUpdating;

  return (
    <Grid container sx={{ minHeight: "100vh", justifyContent: "center", alignItems: { xs: "flex-end", sm: "center" }, p: { xs: 0, sm: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: '600px' },
          borderRadius: { xs: '24px 24px 0 0', sm: 6 },
          boxShadow: theme => theme.palette.mode === 'light'
            ? "0px 20px 60px rgba(0, 0, 0, 0.15)"
            : `0px 20px 60px ${alpha(theme.palette.common.black, 0.5)}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '95vh'
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: isNew ? 'secondary.main' : 'primary.main', borderRadius: 2 }}>
              <AdminPanelSettingsRounded />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{isNew ? "Register Role" : "Update Role"}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Configure system authorization levels</Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleModalClose} size="small"><Close /></IconButton>
        </Box>

        <Box sx={{ p: { xs: 3, sm: 4 }, overflowY: 'auto', flex: 1 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={4}>
              <FormSection title="Role Identity" icon={<ShieldRounded fontSize="inherit" />}>
                <Box sx={{ mt: 2 }}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{
                      required: "Role name is required",
                      pattern: { value: /^\S+$/, message: "No whitespace allowed" }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Unique Role Key"
                        placeholder="e.g. system_auditor"
                        disabled={!isNew}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                      />
                    )}
                  />
                </Box>
              </FormSection>

              <FormSection title="Permissions Management" icon={<PlaylistAddCheckRounded fontSize="inherit" />} hint="Select granular permissions for this role.">
                <Box sx={{ mt: 2 }}>
                  {categories.map((category) => (
                    <Box key={category} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                        {category}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {permissions.filter(p => (p.category || 'System') === category).map((perm) => (
                          <FormControlLabel
                            key={perm.id}
                            label={
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                                {perm.name}
                              </Typography>
                            }
                            sx={{
                              m: 0,
                              p: '4px 12px 4px 4px',
                              bgcolor: 'action.hover',
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              '&:hover': { bgcolor: 'action.selected' }
                            }}
                            control={
                              <Checkbox
                                {...register("permissions_id")}
                                value={perm.id}
                                size="small"
                                sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                              />
                            }
                          />
                        ))}
                      </Box>
                      <Divider sx={{ mt: 2, opacity: 0.5 }} />
                    </Box>
                  ))}
                  {permissions.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', py: 2 }}>
                      No granular permissions available for selection.
                    </Typography>
                  )}
                </Box>
              </FormSection>

              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button fullWidth variant="outlined" color="inherit" onClick={handleModalClose} sx={{ borderRadius: 3, py: 1.2, fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={actionLoading}
                  sx={{
                    borderRadius: 3,
                    py: 1.2,
                    fontWeight: 700,
                    textTransform: 'none',
                    bgcolor: 'secondary.main',
                    '&:hover': { bgcolor: 'secondary.dark' },
                    boxShadow: theme => `0 8px 20px ${alpha(theme.palette.common.black, 0.2)}`
                  }}
                >
                  {actionLoading ? <CircularProgress size={24} color="inherit" /> : (isNew ? "Register" : "Update")}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Paper>
    </Grid>
  );
}

function FormSection({ title, icon, hint, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: 'primary.main', fontSize: 18, display: 'flex' }}>{icon}</Box>
        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography>
        {hint && (
          <Tooltip title={hint} arrow>
            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        )}
      </Stack>
      {children}
    </Box>
  );
}

export default RolesUpdate;
